import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, User } from "lucide-react";
import SeoHead from "../components/SeoHead";

const REDDIT = {
  accutane: "https://www.reddit.com/r/Accutane/",
  euro: "https://www.reddit.com/r/EuroSkincare/",
};

function AboutJsonLd() {
  useEffect(() => {
    const id = "about-person-jsonld";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Person",
      name: "Meghana",
      jobTitle: "PharmD student",
      affiliation: {
        "@type": "CollegeOrUniversity",
        name: "Malla Reddy College of Pharmacy",
      },
      worksFor: { "@type": "Organization", name: "BlendSafe" },
      url: `${window.location.origin}/about`,
    });
    return () => {
      const t = document.getElementById(id);
      if (t && t.parentNode) t.parentNode.removeChild(t);
    };
  }, []);
  return null;
}

const A = (props) => <a {...props} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#1f3d2e] underline underline-offset-2" />;

export default function About() {
  return (
    <main className="bg-cream-50 min-h-[80vh]" data-testid="about-page">
      <SeoHead
        title="Who checks the data — BlendSafe"
        description="Research is prepared by Meghana, a PharmD student, against the FDA label and published dermatology literature. Every published result is reviewed by a licensed US pharmacist before it goes live."
        canonicalPath="/about"
      />
      <AboutJsonLd />

      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-[-0.03em] text-ink leading-[1.05]">
          Who checks the data
        </h1>

        {/* Photo placeholder + name line */}
        <div className="mt-8 flex items-center gap-4">
          <div
            data-testid="about-photo-placeholder"
            className="w-20 h-20 rounded-2xl bg-[#eef3ec] border border-surface-line flex items-center justify-center shrink-0"
          >
            <User className="w-8 h-8 text-[#1f3d2e]/50" />
          </div>
          <p className="text-[15px] font-semibold text-ink leading-snug">
            Meghana — PharmD student, 2nd year, Malla Reddy College of Pharmacy, Hyderabad
          </p>
        </div>

        {/* Bio */}
        <div className="blog-prose mt-9 text-ink-soft" data-testid="about-bio">
          <p>
            Every interaction BlendSafe publishes starts with me. I research it against
            the FDA label and the published dermatology literature, and if I can&rsquo;t find a
            source, it doesn&rsquo;t move forward. A licensed US pharmacist then reviews every
            result before it goes live. I began with isotretinoin and doxycycline — the two
            medications people ask about most, and the two most likely to turn an ordinary
            skincare routine into a problem.
          </p>
          <p>
            I started doing this after time in <A href={REDDIT.accutane}>r/Accutane</A>. The same
            questions kept appearing.{" "}
            <A href={REDDIT.accutane}>Is it okay to use a salicylic acid face wash during Accutane?</A>{" "}
            Then, a few threads later, someone who{" "}
            <A href={REDDIT.accutane}>burnt their face with a salicylic acid cleanser</A> they&rsquo;d
            used for years without trouble. People finishing a course asking{" "}
            <A href={REDDIT.accutane}>when they could safely restart exfoliants</A>. And a long
            thread of <A href={REDDIT.euro}>products that finally helped sensitive Accutane skin</A>,
            built entirely by strangers comparing notes.
          </p>
          <p>
            The dermatologist had covered the blood tests and the pregnancy warnings. Nobody had
            covered the bathroom shelf. That gap is what BlendSafe exists to close.
          </p>
          <p>
            I&rsquo;m a student, not a licensed pharmacist. Nothing here replaces your own doctor or
            pharmacist. It&rsquo;s meant to help you walk into that appointment with better questions.
          </p>
        </div>

        <Link
          to="/check"
          data-testid="about-cta"
          className="mt-10 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-ink text-cream-50 text-[14px] font-bold hover:bg-ink-soft transition-all"
        >
          Check your skincare <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </main>
  );
}
